import { ICheckableTally } from 'interfaces/i-contexts';
import { HasId } from 'lib_ts/interfaces/common/i-extensions';
import { clone } from 'lodash';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { v4 } from 'uuid';

const CONTEXT_NAME = 'CheckedContext';

export interface ICheckedContext {
  // context ID to avoid checkbox labels interfering w/ other contexts' checkboxes by accident
  id: string;

  // when something within the data changes (e.g. check/uncheck 1 item) and only the item + tally needs to change
  tally: ICheckableTally;

  // the last item that was checked or unchecked (used as a reference for shift-click bulk selection)
  lastCheckedIndex: number | null;
  readonly setLastCheckedIndex: (value: number | null) => void;

  checkedIDs: string[];

  // checked IDs in the order they were selected (most recent last), used when
  // selection order matters (e.g. queueing pitches in selection order)
  checkedOrder: string[];

  readonly getChecked: (id: string) => boolean;
  readonly setChecked: (ids: string[], value: boolean) => void;
  readonly resetChecked: () => void;
}

const DEFAULT: ICheckedContext = {
  id: 'none',

  tally: {
    key: Date.now(),
    checked: 0,
    total: 0,
    checkAllState: false,
  },

  lastCheckedIndex: null,
  setLastCheckedIndex: () => console.error(`${CONTEXT_NAME}: not init`),

  checkedIDs: [],
  checkedOrder: [],
  getChecked: () => false,
  setChecked: () => console.error(`${CONTEXT_NAME}: not init`),
  resetChecked: () => console.error(`${CONTEXT_NAME}: not init`),
};

export const CheckedContext = createContext(DEFAULT);

interface IProps {
  data: HasId[];
  children: ReactNode;
}

export const CheckedProvider: FC<IProps> = (props) => {
  const [id] = useState(v4());

  const [dict, setDict] = useState<{
    [id: string]: boolean | undefined;
  }>({});

  const itemIDs = useMemo(() => props.data.map((m) => m._id), [props.data]);

  const tally = useMemo(() => {
    const checked = Object.keys(dict)
      // found in valid ids list (i.e. omit deleted stuff)
      .filter((id) => itemIDs.includes(id))
      // marked as true (i.e. omit unchecked stuff)
      .filter((id) => dict[id]).length;

    const o: ICheckableTally = {
      key: Date.now(),
      checked: checked,
      total: props.data.length,
      checkAllState:
        props.data.length === 0 || checked === 0
          ? false
          : props.data.length === checked
            ? true
            : 'indeterminate',
    };

    return o;
  }, [itemIDs, dict]);

  const [lastCheckedIndex, setLastCheckedIndex] = useState(
    DEFAULT.lastCheckedIndex
  );

  // tracks the order in which items were checked (most recent last); kept in
  // sync with `dict` via setChecked/resetChecked
  const [checkedOrder, setCheckedOrder] = useState<string[]>([]);

  const getChecked = useCallback((id: string) => !!dict[id], [dict]);

  const checkedIDs = useMemo(() => {
    return Object.keys(dict).filter((id) => !!dict[id]);
  }, [dict]);

  const state: ICheckedContext = {
    id: id,
    tally: tally,

    lastCheckedIndex: lastCheckedIndex,
    setLastCheckedIndex: setLastCheckedIndex,

    checkedIDs: checkedIDs,
    checkedOrder: checkedOrder,
    getChecked: getChecked,
    setChecked: (ids, value) => {
      if (ids.length === 0) {
        return;
      }

      setDict((prev) => {
        const newDict = clone(prev);

        ids.forEach((id) => {
          newDict[id] = value;
        });

        return newDict;
      });

      setCheckedOrder((prev) => {
        // drop the affected ids from their current positions...
        const withoutThese = prev.filter((id) => !ids.includes(id));

        // ...then, if checking, re-append them so the most recently checked
        // items end up at the end of the selection order
        return value ? [...withoutThese, ...ids] : withoutThese;
      });
    },

    resetChecked: () => {
      setDict({});
      setCheckedOrder([]);
      setLastCheckedIndex(DEFAULT.lastCheckedIndex);
    },
  };

  return (
    <CheckedContext.Provider value={state}>
      {props.children}
    </CheckedContext.Provider>
  );
};
